/**
 * Application Routes
 * Defines all routes with lazy loading and role-based access control
 */

import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Loading } from '../components/common';
import ProtectedRoute from './ProtectedRoute';
import RoleBasedRoute from './RoleBasedRoute';
import { ROUTES } from './routeConfig';

// Lazy load pages for better performance
// Public pages
const HomePage = lazy(() => import('../pages/public/HomePage'));

// Auth pages
const LoginPage = lazy(() => import('../pages/auth/LoginPage'));

// Student pages
const StudentDashboardPage = lazy(() => import('../pages/student/StudentDashboardPage'));
const StudentHomeworkPage = lazy(() => import('../pages/student/HomeworkPage'));
const StudentHomeworkDetailPage = lazy(() => import('../pages/student/HomeworkDetailPage'));
const StudentHomeworkSubmitPage = lazy(() => import('../pages/student/HomeworkSubmitPage'));
const StudentCalendarPage = lazy(() => import('../pages/student/CalendarPage'));
const StudentTimetablePage = lazy(() => import('../pages/student/TimetablePage'));
const StudentSubmissionDetailPage = lazy(() => import('../pages/student/SubmissionDetailPage'));
const StudentSubmissionsPage = lazy(() => import('../pages/student/SubmissionsPage'));
const StudentProfilePage = lazy(() => import('../pages/student/ProfilePage'));
const StudentSettingsPage = lazy(() => import('../pages/student/SettingsPage'));
const StudentExamsPage = lazy(() => import('../pages/student/ExamsPage'));
const StudentExamDetailPage = lazy(() => import('../pages/student/ExamDetailPage'));
const StudentTakeExamPage = lazy(() => import('../pages/student/TakeExamPage'));
const StudentStudyCardsPage = lazy(() => import('../pages/student/StudyCardsPage'));
const StudentStudyCardsAnalysisPage = lazy(() => import('../pages/student/StudyCardsAnalysisPage'));
const StudentStudyCardTopicPage = lazy(() => import('../pages/student/StudyCardTopicPage'));
const StudentStudyCardDetailPage = lazy(() => import('../pages/student/StudyCardDetailPage'));
const StudentStudyCardStudyPage = lazy(() => import('../pages/student/StudyCardStudyPage'));
const StudentResultsPage = lazy(() => import('../pages/student/ResultsPage'));
const StudentResultDetailPage = lazy(() => import('../pages/student/ResultDetailPage'));
const StudentProgressPage = lazy(() => import('../pages/student/ProgressPage'));

const HkdseIctPage = lazy(() => import('../pages/student/HkdseIctPage'));
const HkdseIctTopicPage = lazy(() => import('../pages/student/HkdseIctTopicPage'));
const HkdseIctNotesPage = lazy(() => import('../pages/student/HkdseIctNotesPage'));
const HkdseIctNotesTopicPage = lazy(() => import('../pages/student/HkdseIctNotesTopicPage'));
const HkdseIctYearsPage = lazy(() => import('../pages/student/HkdseIctYearsPage'));
const HkdseIctYearsTopicPage = lazy(() => import('../pages/student/HkdseIctYearsTopicPage'));
const PdfCabinetPage = lazy(() => import('../pages/student/PdfCabinetPage'));
const PdfCabinetTopicPage = lazy(() => import('../pages/student/PdfCabinetTopicPage'));
const MathsPdfCabinetPage = lazy(() => import('../pages/student/MathsPdfCabinetPage'));
const MathsPdfCabinetTopicPage = lazy(() => import('../pages/student/MathsPdfCabinetTopicPage'));
const SchoolMathsPdfCabinetPage = lazy(() => import('../pages/student/SchoolMathsPdfCabinetPage'));
const SchoolMathsPdfCabinetTopicPage = lazy(
  () => import('../pages/student/SchoolMathsPdfCabinetTopicPage')
);
const HeepYunnPage = lazy(() => import('../pages/student/HeepYunnPage'));
const HeepYunnTopicPage = lazy(() => import('../pages/student/HeepYunnTopicPage'));
const HeepYunnF1Page = lazy(() => import('../pages/student/HeepYunnF1Page'));
const HeepYunnF1TopicPage = lazy(() => import('../pages/student/HeepYunnF1TopicPage'));
const GoodHopePage = lazy(() => import('../pages/student/GoodHopePage'));
const GoodHopeTopicPage = lazy(() => import('../pages/student/GoodHopeTopicPage'));
const GoodHopeF1Page = lazy(() => import('../pages/student/GoodHopeF1Page'));
const GoodHopeF1TopicPage = lazy(() => import('../pages/student/GoodHopeF1TopicPage'));
const HkdseMathsP1Page = lazy(() => import('../pages/student/HkdseMathsP1Page'));
const HkdseMathsP1AnalysisPage = lazy(() => import('../pages/student/HkdseMathsP1AnalysisPage'));
const HkdseMathsP1LqSkillBooksPage = lazy(() => import('../pages/student/HkdseMathsP1LqSkillBooksPage'));
const HkdseMathsP1TopicPage = lazy(() => import('../pages/student/HkdseMathsP1TopicPage'));
const ExamRightP1Page = lazy(() => import('../pages/student/ExamRightP1Page'));
const ExamRightP1TopicPage = lazy(() => import('../pages/student/ExamRightP1TopicPage'));
const ExamRightP2McPage = lazy(() => import('../pages/student/ExamRightP2McPage'));
const ExamRightP2McTopicPage = lazy(() => import('../pages/student/ExamRightP2McTopicPage'));
const DseRightP1Page = lazy(() => import('../pages/student/DseRightP1Page'));
const DseRightP1AnalysisPage = lazy(() => import('../pages/student/DseRightP1AnalysisPage'));
const DseRightP1LqSkillBooksPage = lazy(() => import('../pages/student/DseRightP1LqSkillBooksPage'));
const DseRightP1TopicPage = lazy(() => import('../pages/student/DseRightP1TopicPage'));
const DseRightP2McPage = lazy(() => import('../pages/student/DseRightP2McPage'));
const DseRightP2McAnalysisPage = lazy(() => import('../pages/student/DseRightP2McAnalysisPage'));
const DseRightP2McSkillBooksPage = lazy(() => import('../pages/student/DseRightP2McSkillBooksPage'));
const DseRightP2McTopicPage = lazy(() => import('../pages/student/DseRightP2McTopicPage'));
const HkdseMathsP2Page = lazy(() => import('../pages/student/HkdseMathsP2Page'));
const HkdseMathsP2AnalysisPage = lazy(() => import('../pages/student/HkdseMathsP2AnalysisPage'));
const HkdseMathsP2McqSkillBooksPage = lazy(() => import('../pages/student/HkdseMathsP2McqSkillBooksPage'));
const HkdseMathsP2TopicPage = lazy(() => import('../pages/student/HkdseMathsP2TopicPage'));
const HkdseMathsP1TopicsPage = lazy(() => import('../pages/student/HkdseMathsP1TopicsPage'));
const HkdseMathsP1TopicsTopicPage = lazy(() => import('../pages/student/HkdseMathsP1TopicsTopicPage'));
const HkdseMathsP2TopicsPage = lazy(() => import('../pages/student/HkdseMathsP2TopicsPage'));
const HkdseMathsP2TopicsTopicPage = lazy(() => import('../pages/student/HkdseMathsP2TopicsTopicPage'));

// Teacher pages
const TeacherDashboardPage = lazy(() => import('../pages/teacher/TeacherDashboardPage'));
const TeacherHomeworkPage = lazy(() => import('../pages/teacher/HomeworkPage'));
const TeacherHomeworkCreatePage = lazy(() => import('../pages/teacher/HomeworkCreatePage'));
const TeacherHomeworkEditPage = lazy(() => import('../pages/teacher/HomeworkEditPage'));
const TeacherHomeworkDetailPage = lazy(() => import('../pages/teacher/HomeworkDetailPage'));
const TeacherClassPage = lazy(() => import('../pages/teacher/ClassPage'));
const TeacherClassDetailPage = lazy(() => import('../pages/teacher/ClassDetailPage'));
const TeacherTimetablePage = lazy(() => import('../pages/teacher/TeacherTimetablePage'));
const TeacherProfilePage = lazy(() => import('../pages/teacher/ProfilePage'));
const TeacherSettingsPage = lazy(() => import('../pages/student/SettingsPage'));
const TeacherExamsPage = lazy(() => import('../pages/teacher/ExamsPage'));
const TeacherExamCreatePage = lazy(() => import('../pages/teacher/ExamCreatePage'));
const TeacherExamEditPage = lazy(() => import('../pages/teacher/ExamEditPage'));
const TeacherExamAnalyticsPage = lazy(() => import('../pages/teacher/ExamAnalyticsPage'));
const TeacherStudyCardsPage = lazy(() => import('../pages/teacher/StudyCardsPage'));
const TeacherStudyCardsAnalysisPage = lazy(() => import('../pages/teacher/StudyCardsAnalysisPage'));
const TeacherStudyCardTopicPage = lazy(() => import('../pages/teacher/StudyCardTopicPage'));
const TeacherStudyCardEditPage = lazy(() => import('../pages/teacher/StudyCardEditPage'));
const AdminCorsApiTesterPage = lazy(() => import('../pages/admin/CorsApiTesterPage'));
const AdminDseRightHtmlEditorPage = lazy(() => import('../pages/admin/DseRightHtmlEditorPage'));
const TeacherQuestionsPage = lazy(() => import('../pages/teacher/QuestionsPage'));
const TeacherQuestionBankEditorPage = lazy(() => import('../pages/teacher/QuestionBankEditorPage'));
const TeacherGradingPage = lazy(() => import('../pages/teacher/GradingPage'));

// Error pages
const NotFoundPage = lazy(() => import('../pages/errors/NotFoundPage'));
const UnauthorizedPage = lazy(() => import('../pages/errors/UnauthorizedPage'));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background-app">
    <Loading size="lg" text="Loading..." />
  </div>
);

const AppRoutes = () => {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public Routes */}
        <Route path={ROUTES.HOME} element={<HomePage />} />
        
        {/* Auth Routes */}
        <Route path={ROUTES.AUTH.LOGIN} element={<LoginPage />} />
        
        {/* Student Routes */}
        <Route
          path={ROUTES.STUDENT.DASHBOARD}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <StudentDashboardPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.HOMEWORK}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <StudentHomeworkPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.EXAMS}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <StudentExamsPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.EXAM_DETAIL}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <StudentExamDetailPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.TAKE_EXAM}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <StudentTakeExamPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.STUDYCARDS}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <StudentStudyCardsPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.STUDYCARDS_ANALYSIS}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <StudentStudyCardsAnalysisPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.STUDYCARD_TOPIC}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <StudentStudyCardTopicPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.STUDYCARD_EDIT}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <TeacherStudyCardEditPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.STUDYCARD_DETAIL}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <StudentStudyCardDetailPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.STUDYCARD_STUDY}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <StudentStudyCardStudyPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.RESULTS}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <StudentResultsPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.RESULT_DETAIL}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <StudentResultDetailPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.PROGRESS}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <StudentProgressPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.CALENDAR}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <StudentCalendarPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.TIMETABLE}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <StudentTimetablePage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.HOMEWORK_DETAIL}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <StudentHomeworkDetailPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.HOMEWORK_SUBMIT}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <StudentHomeworkSubmitPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.SUBMISSION_DETAIL}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <StudentSubmissionDetailPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.SUBMISSIONS}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <StudentSubmissionsPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.PROFILE}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <StudentProfilePage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.SETTINGS}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <StudentSettingsPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.HKDSE_ICT}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <HkdseIctPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.HKDSE_ICT_TOPIC}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <HkdseIctTopicPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.HKDSE_ICT_NOTES}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <HkdseIctNotesPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.HKDSE_ICT_NOTES_TOPIC}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <HkdseIctNotesTopicPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.HKDSE_ICT_YEARS}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <HkdseIctYearsPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.HKDSE_ICT_YEARS_TOPIC}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <HkdseIctYearsTopicPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.PDF_CABINET}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <PdfCabinetPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.PDF_CABINET_FILE}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <PdfCabinetTopicPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.MATHS_PDF_CABINET}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <MathsPdfCabinetPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.MATHS_PDF_CABINET_FILE}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <MathsPdfCabinetTopicPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.SCHOOL_MATHS_PDF_CABINET}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <SchoolMathsPdfCabinetPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.SCHOOL_MATHS_PDF_CABINET_FILE}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <SchoolMathsPdfCabinetTopicPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.HEEP_YUNN}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <HeepYunnPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.HEEP_YUNN_TOPIC}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <HeepYunnTopicPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.HEEP_YUNN_F1}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <HeepYunnF1Page />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.HEEP_YUNN_F1_TOPIC}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <HeepYunnF1TopicPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.GOOD_HOPE}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <GoodHopePage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.GOOD_HOPE_TOPIC}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <GoodHopeTopicPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.GOOD_HOPE_F1}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <GoodHopeF1Page />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.GOOD_HOPE_F1_TOPIC}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <GoodHopeF1TopicPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.HKDSE_MATHS_P1}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <HkdseMathsP1Page />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.HKDSE_MATHS_P1_ANALYSIS}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <HkdseMathsP1AnalysisPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.HKDSE_MATHS_P1_LQ_SKILL_BOOKS}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <HkdseMathsP1LqSkillBooksPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.HKDSE_MATHS_P1_TOPIC}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <HkdseMathsP1TopicPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.EXAMRIGHT_P1}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student', 'teacher', 'admin']}>
                <ExamRightP1Page />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.EXAMRIGHT_P1_TOPIC}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student', 'teacher', 'admin']}>
                <ExamRightP1TopicPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.EXAMRIGHT_P2_MC}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student', 'teacher', 'admin']}>
                <ExamRightP2McPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.EXAMRIGHT_P2_MC_TOPIC}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student', 'teacher', 'admin']}>
                <ExamRightP2McTopicPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.DSERIGHT_P1}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student', 'teacher', 'admin']}>
                <DseRightP1Page />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.DSERIGHT_P1_ANALYSIS}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student', 'teacher', 'admin']}>
                <DseRightP1AnalysisPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.DSERIGHT_P1_LQ_SKILL_BOOKS}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student', 'teacher', 'admin']}>
                <DseRightP1LqSkillBooksPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.DSERIGHT_P1_TOPIC}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student', 'teacher', 'admin']}>
                <DseRightP1TopicPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.DSERIGHT_P2_MC}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student', 'teacher', 'admin']}>
                <DseRightP2McPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.DSERIGHT_P2_MC_ANALYSIS}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student', 'teacher', 'admin']}>
                <DseRightP2McAnalysisPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.DSERIGHT_P2_MC_SKILL_BOOKS}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student', 'teacher', 'admin']}>
                <DseRightP2McSkillBooksPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.DSERIGHT_P2_MC_TOPIC}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student', 'teacher', 'admin']}>
                <DseRightP2McTopicPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.HKDSE_MATHS_P2}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <HkdseMathsP2Page />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.HKDSE_MATHS_P2_ANALYSIS}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <HkdseMathsP2AnalysisPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.HKDSE_MATHS_P2_MCQ_SKILL_BOOKS}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <HkdseMathsP2McqSkillBooksPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.HKDSE_MATHS_P2_TOPIC}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <HkdseMathsP2TopicPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.HKDSE_MATHS_P1_BY_TOPICS}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <HkdseMathsP1TopicsPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.HKDSE_MATHS_P1_BY_TOPICS_TOPIC}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <HkdseMathsP1TopicsTopicPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.HKDSE_MATHS_P2_BY_TOPICS}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <HkdseMathsP2TopicsPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.HKDSE_MATHS_P2_BY_TOPICS_TOPIC}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['student']}>
                <HkdseMathsP2TopicsTopicPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        
        {/* Teacher Routes */}
        <Route
          path={ROUTES.TEACHER.DASHBOARD}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['teacher', 'admin', 'staff']}>
                <TeacherDashboardPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.HOMEWORK}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['teacher', 'admin', 'staff']}>
                <TeacherHomeworkPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.HOMEWORK_CREATE}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['teacher', 'admin', 'staff']}>
                <TeacherHomeworkCreatePage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.HOMEWORK_EDIT}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['teacher', 'admin', 'staff']}>
                <TeacherHomeworkEditPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.HOMEWORK_DETAIL}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['teacher', 'admin', 'staff']}>
                <TeacherHomeworkDetailPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.EXAMS}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['teacher', 'admin', 'staff']}>
                <TeacherExamsPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.EXAM_CREATE}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['teacher', 'admin', 'staff']}>
                <TeacherExamCreatePage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.EXAM_EDIT}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['teacher', 'admin', 'staff']}>
                <TeacherExamEditPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.STUDYCARDS}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['teacher', 'admin', 'staff']}>
                <TeacherStudyCardsPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.STUDYCARDS_ANALYSIS}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['teacher', 'admin', 'staff']}>
                <TeacherStudyCardsAnalysisPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.STUDYCARD_TOPIC}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['teacher', 'admin', 'staff']}>
                <TeacherStudyCardTopicPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.STUDYCARD_EDIT}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['teacher', 'admin', 'staff']}>
                <TeacherStudyCardEditPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.STUDYCARD_STUDY}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['teacher', 'admin', 'staff']}>
                <StudentStudyCardStudyPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.API_TESTER}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['admin']}>
                <AdminCorsApiTesterPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.DSERIGHT_HTML_EDITOR}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['admin', 'staff']}>
                <AdminDseRightHtmlEditorPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.EXAM_ANALYTICS}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['teacher', 'admin', 'staff']}>
                <TeacherExamAnalyticsPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.QUESTIONS}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['teacher', 'admin', 'staff']}>
                <TeacherQuestionsPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.QUESTION_BANK_EDITOR}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['teacher', 'admin', 'staff']}>
                <TeacherQuestionBankEditorPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.QUESTION_BANK_EDITOR_TABLE}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['teacher', 'admin', 'staff']}>
                <TeacherQuestionBankEditorPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.GRADING}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['teacher', 'admin', 'staff']}>
                <TeacherGradingPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.CLASSES}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['teacher', 'admin', 'staff']}>
                <TeacherClassPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.CLASS_DETAIL}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['teacher', 'admin', 'staff']}>
                <TeacherClassDetailPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.TIMETABLE}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['teacher', 'admin', 'staff']}>
                <TeacherTimetablePage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.PROFILE}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['teacher', 'admin', 'staff']}>
                <TeacherProfilePage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.SETTINGS}
          element={
            <ProtectedRoute>
              <RoleBasedRoute allowedRoles={['teacher', 'admin', 'staff']}>
                <TeacherSettingsPage />
              </RoleBasedRoute>
            </ProtectedRoute>
          }
        />
        
        {/* Error Routes */}
        <Route path={ROUTES.ERRORS.NOT_FOUND} element={<NotFoundPage />} />
        <Route path={ROUTES.ERRORS.UNAUTHORIZED} element={<UnauthorizedPage />} />
        
        {/* Default redirect */}
        <Route path="*" element={<Navigate to={ROUTES.ERRORS.NOT_FOUND} replace />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;

